// Products API
import { api, apiGet, apiPost, apiPatch, apiDelete } from './client'
import { handleMockRoute } from './mockData'

export const productsApi = {
  // Products
  list: async (params?: ProductListParams) => {
    try {
      return await apiGet<PaginatedResponse<Product>>('/products', params)
    } catch (err) {
      console.warn('Backend API list products error, falling back to mock data:', err)
      return handleMockRoute('GET', '/products', params)
    }
  },

  get: async (id: string) => {
    try {
      return await apiGet<ProductDetail>(`/products/${id}`)
    } catch (err) {
      console.warn('Backend API get product error, falling back to mock data:', err)
      return handleMockRoute('GET', `/products/${id}`)
    }
  },

  create: async (data: ProductCreate) => {
    try {
      return await apiPost<Product>('/products', data)
    } catch (err) {
      console.warn('Backend API create product error, falling back to mock store:', err)
      return handleMockRoute('POST', '/products', data)
    }
  },

  update: async (id: string, data: ProductUpdate) => {
    try {
      return await apiPatch<Product>(`/products/${id}`, data)
    } catch (err) {
      console.warn('Backend API update product error, falling back to mock store:', err)
      return handleMockRoute('PATCH', `/products/${id}`, data)
    }
  },

  delete: async (id: string) => {
    try {
      return await apiDelete(`/products/${id}`)
    } catch (err) {
      console.warn('Backend API delete product error, falling back to mock store:', err)
      return handleMockRoute('DELETE', `/products/${id}`)
    }
  },

  bulkCreate: (data: ProductCreate[]) =>
    apiPost<Product[]>('/products/bulk', data),

  bulkUpdate: (data: { ids: string[]; data: ProductUpdate }) =>
    api.patch('/products/bulk', data).then(res => res.data),

  bulkDelete: (ids: string[]) =>
    api.delete('/products/bulk', { data: { ids } }).then(res => res.data),

  // Variants
  listVariants: (productId: string, params?: PaginationParams) =>
    apiGet<PaginatedResponse<ProductVariant>>(`/products/${productId}/variants`, params),

  getVariant: (productId: string, variantId: string) =>
    apiGet<ProductVariant>(`/products/${productId}/variants/${variantId}`),

  createVariant: (productId: string, data: ProductVariantCreate) =>
    apiPost<ProductVariant>(`/products/${productId}/variants`, data),

  updateVariant: (productId: string, variantId: string, data: ProductVariantUpdate) =>
    apiPatch<ProductVariant>(`/products/${productId}/variants/${variantId}`, data),

  deleteVariant: (productId: string, variantId: string) =>
    apiDelete(`/products/${productId}/variants/${variantId}`),

  // Categories
  listCategories: (params?: CategoryListParams) =>
    apiGet<PaginatedResponse<Category>>('/products/categories', params),

  getCategory: (id: string) =>
    apiGet<Category>(`/products/categories/${id}`),

  createCategory: (data: CategoryCreate) =>
    apiPost<Category>('/products/categories', data),

  updateCategory: (id: string, data: CategoryUpdate) =>
    apiPatch<Category>(`/products/categories/${id}`, data),

  deleteCategory: (id: string) =>
    apiDelete(`/products/categories/${id}`),

  // Brands
  listBrands: (params?: BrandListParams) =>
    apiGet<PaginatedResponse<Brand>>('/products/brands', params),

  getBrand: (id: string) =>
    apiGet<Brand>(`/products/brands/${id}`),

  createBrand: (data: BrandCreate) =>
    apiPost<Brand>('/products/brands', data),

  updateBrand: (id: string, data: BrandUpdate) =>
    apiPatch<Brand>(`/products/brands/${id}`, data),

  deleteBrand: (id: string) =>
    apiDelete(`/products/brands/${id}`),

  // Units
  listUnits: () =>
    apiGet<Unit[]>('/products/units'),

  createUnit: (data: UnitCreate) =>
    apiPost<Unit>('/products/units', data),

  // Import/Export
  import: (file: File, onProgress?: (p: number) => void) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/products/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / (e.total || 1))),
    }).then(res => res.data)
  },

  export: (params?: ProductListParams) =>
    api.get('/products/export', { params, responseType: 'blob' }).then(res => res.data),
}

// Types
export interface PaginationParams {
  page?: number
  page_size?: number
  search?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface ProductListParams extends PaginationParams {
  category_id?: string
  brand_id?: string
  type?: string
  is_active?: boolean
  low_stock?: boolean
}

export interface CategoryListParams extends PaginationParams {
  parent_id?: string
  is_active?: boolean
}

export interface BrandListParams extends PaginationParams {
  is_active?: boolean
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface Product {
  id: string
  sku: string
  name: string
  description?: string
  type: 'simple' | 'variant' | 'bundle' | 'service' | 'digital'
  category_id?: string
  brand_id?: string
  unit_id?: string
  barcode?: string
  weight?: number
  images: string[]
  tags: string[]
  is_active: boolean
  is_sellable: boolean
  is_purchasable: boolean
  track_stock: boolean
  min_stock?: number
  max_stock?: number
  reorder_point?: number
  reorder_quantity?: number
  cost_price?: number
  sale_price?: number
  tax_rate: number
  current_stock: number
  available_stock: number
  variants_count: number
  category?: Category
  brand?: Brand
  unit?: Unit
  created_at: string
  updated_at: string
}

export interface ProductDetail extends Product {
  variants: ProductVariant[]
  warehouse_stocks: WarehouseStock[]
}

export interface ProductCreate {
  sku: string
  name: string
  description?: string
  type?: 'simple' | 'variant' | 'bundle' | 'service' | 'digital'
  category_id?: string
  brand_id?: string
  unit_id?: string
  barcode?: string
  weight?: number
  dimensions?: { length: number; width: number; height: number }
  tags?: string[]
  images?: string[]
  is_active?: boolean
  is_sellable?: boolean
  is_purchasable?: boolean
  track_stock?: boolean
  min_stock?: number
  max_stock?: number
  reorder_point?: number
  reorder_quantity?: number
  cost_price?: number
  sale_price?: number
  tax_rate?: number
  variant_attributes?: VariantAttribute[]
}

export interface ProductUpdate {
  name?: string
  description?: string
  category_id?: string
  brand_id?: string
  unit_id?: string
  barcode?: string
  weight?: number
  dimensions?: { length: number; width: number; height: number }
  tags?: string[]
  images?: string[]
  is_active?: boolean
  is_sellable?: boolean
  is_purchasable?: boolean
  track_stock?: boolean
  min_stock?: number
  max_stock?: number
  reorder_point?: number
  reorder_quantity?: number
  cost_price?: number
  sale_price?: number
  tax_rate?: number
}

export interface VariantAttribute {
  name: string
  value: string
  display_order?: number
  color_code?: string
  image_url?: string
}

export interface ProductVariant {
  id: string
  product_id: string
  sku: string
  name: string
  attributes: VariantAttribute[]
  barcode?: string
  cost_price?: number
  sale_price?: number
  weight?: number
  is_active: boolean
  current_stock: number
  available_stock: number
  created_at: string
  updated_at: string
}

export interface ProductVariantCreate {
  sku: string
  name: string
  attributes: VariantAttribute[]
  barcode?: string
  cost_price?: number
  sale_price?: number
  weight?: number
  is_active?: boolean
}

export interface ProductVariantUpdate {
  name?: string
  attributes?: VariantAttribute[]
  barcode?: string
  cost_price?: number
  sale_price?: number
  weight?: number
  is_active?: boolean
}

export interface Category {
  id: string
  name: string
  slug: string
  description?: string
  parent_id?: string
  image_url?: string
  display_order: number
  is_active: boolean
  children: Category[]
  created_at: string
  updated_at: string
}

export interface CategoryCreate {
  name: string
  slug: string
  description?: string
  parent_id?: string
  image_url?: string
  display_order?: number
  is_active?: boolean
}

export interface CategoryUpdate {
  name?: string
  slug?: string
  description?: string
  parent_id?: string
  image_url?: string
  display_order?: number
  is_active?: boolean
}

export interface Brand {
  id: string
  name: string
  slug: string
  description?: string
  logo_url?: string
  website?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface BrandCreate {
  name: string
  slug: string
  description?: string
  logo_url?: string
  website?: string
  is_active?: boolean
}

export interface BrandUpdate {
  name?: string
  slug?: string
  description?: string
  logo_url?: string
  website?: string
  is_active?: boolean
}

export interface Unit {
  id: string
  name: string
  symbol: string
  description?: string
  is_base: boolean
  conversion_factor: number
  created_at: string
  updated_at: string
}

export interface UnitCreate {
  name: string
  symbol: string
  description?: string
  is_base?: boolean
  conversion_factor?: number
}

export interface WarehouseStock {
  warehouse_id: string
  warehouse_name: string
  quantity: number
  reserved_quantity: number
  available_quantity: number
  min_quantity: number
  max_quantity: number
}